/**
 * Customers screen — list customers and create new ones (with optional primary contact).
 *
 * Fixes the prior gap where customers could only be selected, never created, on mobile (which
 * blocked first-estimate creation for a new tenant). Reached from the Home → More hub and offered
 * inline on the New Estimate screen.
 *
 * NEVER imports @smartbox/* (mobile invariant 1). Design-system primitives only; no raw styles.
 */
import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listCustomers, createCustomer, createContact } from '../../src/api/crm';
import { Screen, Text, Card, Button, Field, Select, ListRow } from '../../src/components/ui';
import { theme } from '../../src/theme';
import { useHelpEntry } from '../../src/help';
import { SCREEN_KEYS } from '../../src/help';
import { extractErrorMessage, humanize } from '../../src/lib/format';

const TYPE_OPTIONS = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'government', label: 'Government' },
] as const;

export default function CustomersScreen() {
  const emptyHelp = useHelpEntry(SCREEN_KEYS.customers, 'empty');
  const router = useRouter();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [type, setType] = useState<'residential' | 'commercial' | 'government'>('residential');
  const [displayName, setDisplayName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const customersQuery = useQuery({ queryKey: ['customers'], queryFn: listCustomers, staleTime: 30_000 });

  const createMutation = useMutation({
    mutationFn: async () => {
      const customer = await createCustomer({
        customer_type: type,
        display_name: displayName.trim(),
        ...(companyName.trim() ? { company_name: companyName.trim() } : {}),
      });
      if (contactName.trim()) {
        await createContact({
          full_name: contactName.trim(),
          customer_id: customer.id,
          is_primary: true,
          ...(contactPhone.trim() ? { phone: contactPhone.trim() } : {}),
          ...(contactEmail.trim() ? { email: contactEmail.trim() } : {}),
        });
      }
      return customer;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      setShowForm(false);
      setDisplayName('');
      setCompanyName('');
      setContactName('');
      setContactPhone('');
      setContactEmail('');
      setFormError(null);
    },
    onError: (err) => setFormError(extractErrorMessage(err, 'Could not create customer.')),
  });

  const onCreate = () => {
    setFormError(null);
    if (!displayName.trim()) {
      setFormError('Enter a customer name.');
      return;
    }
    createMutation.mutate();
  };

  const customers = customersQuery.data ?? [];

  return (
    <Screen scroll>
      <Button
        title={showForm ? 'Cancel' : '+ New customer'}
        variant={showForm ? 'secondary' : 'primary'}
        onPress={() => setShowForm((v) => !v)}
        accessibilityLabel="New customer"
      />

      {showForm ? (
        <Card>
          <Text variant="bodyStrong">New customer</Text>
          <Select label="Type" options={TYPE_OPTIONS} value={type} onChange={(v) => setType(v)} />
          <Field label="Name" placeholder="e.g. Jane Homeowner" value={displayName} onChangeText={setDisplayName} accessibilityLabel="Customer name" />
          <Field label="Company (optional)" placeholder="e.g. Acme LLC" value={companyName} onChangeText={setCompanyName} accessibilityLabel="Company name" />
          <Text variant="label" color="secondary">Primary contact (optional)</Text>
          <Field label="Contact name" placeholder="Full name" value={contactName} onChangeText={setContactName} accessibilityLabel="Contact name" />
          <Field label="Phone" placeholder="+1 555 555 5555" value={contactPhone} onChangeText={setContactPhone} keyboardType="phone-pad" accessibilityLabel="Contact phone" />
          <Field label="Email" placeholder="name@example.com" value={contactEmail} onChangeText={setContactEmail} keyboardType="email-address" autoCapitalize="none" accessibilityLabel="Contact email" />
          {formError ? (
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">{formError}</Text>
          ) : null}
          <Button title="Save customer" onPress={onCreate} loading={createMutation.isPending} disabled={createMutation.isPending} accessibilityLabel="Save customer" />
        </Card>
      ) : null}

      <Text variant="label" color="secondary">
        {customers.length} {customers.length === 1 ? 'customer' : 'customers'}
      </Text>

      {customersQuery.isLoading ? (
        <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading customers" />
      ) : customersQuery.isError ? (
        <Card>
          <Text variant="body" color="danger">{extractErrorMessage(customersQuery.error, 'Failed to load customers.')}</Text>
          <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => customersQuery.refetch()} />
        </Card>
      ) : customers.length === 0 ? (
        <Text variant="body" color="secondary">{emptyHelp?.body ?? 'No customers yet. Tap “New customer” to add your first.'}</Text>
      ) : (
        <View style={styles.list}>
          {customers.map((c) => (
            <ListRow
              key={c.id}
              title={c.displayName}
              subtitle={c.companyName ?? humanize(c.customerType)}
              onPress={() => router.push(`/(app)/customers/${c.id}`)}
              accessibilityLabel={`Open ${c.displayName}`}
            />
          ))}
        </View>
      )}
    </Screen>
  );
}

const styles = StyleSheet.create({
  list: { gap: theme.spacing.sm },
});
